import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { Mail, Phone, Building2, Briefcase, MessageSquare, Trash2, Check, ArrowLeft } from 'lucide-react';
import { API_PREFIX, BACKEND_URL } from '../config';

const SharedContacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${BACKEND_URL}${API_PREFIX}/shared-contacts/my-contacts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement');
      }

      const data = await response.json();
      setContacts(data);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Impossible de charger les contacts');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (contactId) => {
    try {
      const token = localStorage.getItem('access_token');
      
      await fetch(`${BACKEND_URL}${API_PREFIX}/shared-contacts/${contactId}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Update local state
      setContacts(contacts.map(c => 
        c.id === contactId ? { ...c, is_read: true } : c
      ));
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Supprimer ce contact ?')) return;

    try {
      const token = localStorage.getItem('access_token');
      
      await fetch(`${BACKEND_URL}${API_PREFIX}/shared-contacts/${contactId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setContacts(contacts.filter(c => c.id !== contactId));
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const unreadCount = contacts.filter(c => !c.is_read).length;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour au dashboard
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Contacts reçus</h1>
              <p className="text-gray-400">
                {contacts.length} contact{contacts.length !== 1 ? 's' : ''} partagé{contacts.length !== 1 ? 's' : ''}
                {unreadCount > 0 && (
                  <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#2B4A6F]/20 text-[#7CB4E8] border border-[#2B4A6F]/30">
                    {unreadCount} nouveau{unreadCount !== 1 ? 'x' : ''}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-[#2B4A6F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Chargement...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-500">
            {error}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && contacts.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-12 h-12 text-gray-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Aucun contact reçu</h3>
            <p className="text-gray-400 mb-6">
              Les contacts qui partageront leurs coordonnées via votre carte apparaitront ici.
            </p>
          </div>
        )}

        {/* Contacts List */}
        {!loading && !error && contacts.length > 0 && (
          <div className="space-y-4">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className={`bg-gradient-to-br from-[#0A0A0A] to-[#151515] rounded-xl border transition-all hover:border-[#2B4A6F]/50 ${
                  contact.is_read ? 'border-gray-800/50' : 'border-[#2B4A6F]/30 shadow-lg shadow-[#2B4A6F]/10'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{contact.full_name}</h3>
                        {!contact.is_read && (
                          <span className="px-2 py-1 bg-[#2B4A6F]/20 text-[#7CB4E8] text-xs font-medium rounded-full border border-[#2B4A6F]/30">
                            Nouveau
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        Reçu le {formatDate(contact.created_at)}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      {!contact.is_read && (
                        <button
                          onClick={() => markAsRead(contact.id)}
                          className="p-2 text-gray-400 hover:text-green-500 transition-colors"
                          title="Marquer comme lu"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteContact(contact.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Contact Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Email */}
                    <div className="flex items-center space-x-3 text-gray-300">
                      <Mail className="w-5 h-5 text-gray-500" />
                      <a href={`mailto:${contact.email}`} className="hover:text-[#7CB4E8] transition-colors">
                        {contact.email}
                      </a>
                    </div>

                    {/* Phone */}
                    {contact.phone && (
                      <div className="flex items-center space-x-3 text-gray-300">
                        <Phone className="w-5 h-5 text-gray-500" />
                        <a href={`tel:${contact.phone}`} className="hover:text-[#7CB4E8] transition-colors">
                          {contact.phone}
                        </a>
                      </div>
                    )}

                    {/* Company */}
                    {contact.company && (
                      <div className="flex items-center space-x-3 text-gray-300">
                        <Building2 className="w-5 h-5 text-gray-500" />
                        <span>{contact.company}</span>
                      </div>
                    )}

                    {/* Job Title */}
                    {contact.job_title && (
                      <div className="flex items-center space-x-3 text-gray-300">
                        <Briefcase className="w-5 h-5 text-gray-500" />
                        <span>{contact.job_title}</span>
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  {contact.message && (
                    <div className="mt-4 p-4 bg-black/30 rounded-lg border border-gray-800/50">
                      <div className="flex items-start space-x-3">
                        <MessageSquare className="w-5 h-5 text-gray-500 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Message :</p>
                          <p className="text-gray-300 italic">"{contact.message}"</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SharedContacts;
